(() => {
  'use strict';

  if (window.ASCBannerQuickView) {
    window.ASCBannerQuickView.mount(document);
    return;
  }

  const controllers = new Map();
  const lifecycle = new AbortController();
  const productCache = window.ascQuickViewCache instanceof Map
    ? window.ascQuickViewCache
    : new Map();
  const scriptRegistry = window.ascBannerQuickViewScriptRegistry instanceof Map
    ? window.ascBannerQuickViewScriptRegistry
    : new Map();

  window.ascQuickViewCache = productCache;
  window.ascBannerQuickViewScriptRegistry = scriptRegistry;

  let activeController = null;

  Array.from(document.scripts).forEach((script) => {
    if (!script.src || scriptRegistry.has(script.src)) return;
    scriptRegistry.set(script.src, Promise.resolve());
  });

  const getProductSection = (html) => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const productRoot = parsedDocument.querySelector('[id^="AscPdp-"]');
    const productSection = productRoot?.closest('.shopify-section') || productRoot?.parentElement;

    return productRoot && productSection ? productSection : null;
  };

  const inheritPreviewParameters = (url) => {
    const currentUrl = new URL(window.location.href);

    ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => {
      if (!url.searchParams.has(parameter) && currentUrl.searchParams.has(parameter)) {
        url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
      }
    });
  };

  const requestProductMarkup = async (url) => {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'text/html'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Quick View request failed with ${response.status}`);
    }

    return response.text();
  };

  const scopeProductSection = (markup, scopeId) => {
    if (!scopeId) return markup;

    const productSection = getProductSection(markup);
    const sourceSectionId = productSection?.querySelector('[id^="AscPdp-"]')?.dataset.sectionId;
    if (!sourceSectionId) return markup;

    const safeScopeId = scopeId.replace(/[^a-zA-Z0-9_-]/g, '-');
    return markup.split(sourceSectionId).join(`${sourceSectionId}--${safeScopeId}`);
  };

  const fetchConfiguredProductSection = async (productUrl, scopeId = '') => {
    const productPageUrl = new URL(productUrl, window.location.origin);
    const cacheKey = `${productPageUrl.pathname}::configured-product-section`;
    const cachedRequest = productCache.get(cacheKey);
    if (cachedRequest) return scopeProductSection(await cachedRequest, scopeId);

    inheritPreviewParameters(productPageUrl);

    const request = (async () => {
      const knownSectionId = window.ascQuickViewProductSectionId;

      if (knownSectionId) {
        const sectionUrl = new URL(productPageUrl.toString());
        sectionUrl.searchParams.set('section_id', knownSectionId);

        const sectionMarkup = await requestProductMarkup(sectionUrl);
        const productSection = getProductSection(sectionMarkup);

        if (productSection) return productSection.outerHTML;
        window.ascQuickViewProductSectionId = '';
      }

      productPageUrl.searchParams.delete('section_id');
      const pageMarkup = await requestProductMarkup(productPageUrl);
      const productSection = getProductSection(pageMarkup);

      if (!productSection) {
        throw new Error('Configured product section was not found in the Quick View response');
      }

      const sectionId = productSection.id?.replace(/^shopify-section-/, '');
      if (sectionId) window.ascQuickViewProductSectionId = sectionId;

      return productSection.outerHTML;
    })();

    productCache.set(cacheKey, request);

    try {
      return scopeProductSection(await request, scopeId);
    } catch (error) {
      if (productCache.get(cacheKey) === request) productCache.delete(cacheKey);
      throw error;
    }
  };

  if (typeof window.ascFetchConfiguredProductSection !== 'function') {
    window.ascFetchConfiguredProductSection = fetchConfiguredProductSection;
  }

  const createAbortError = () => {
    try {
      return new DOMException('The operation was aborted.', 'AbortError');
    } catch (error) {
      const abortError = new Error('The operation was aborted.');
      abortError.name = 'AbortError';
      return abortError;
    }
  };

  const waitForRequest = (request, signal) => {
    if (!signal) return request;
    if (signal.aborted) return Promise.reject(createAbortError());

    return new Promise((resolve, reject) => {
      const abort = () => reject(createAbortError());
      signal.addEventListener('abort', abort, { once: true });

      request.then(resolve, reject).finally(() => {
        signal.removeEventListener('abort', abort);
      });
    });
  };

  const loadExternalScript = (source, sourceScript) => {
    const absoluteSource = new URL(source, window.location.href).href;
    const registered = scriptRegistry.get(absoluteSource);
    if (registered) return registered;

    const existingScript = Array.from(document.scripts).find((script) => (
      script !== sourceScript && script.src === absoluteSource
    ));

    if (existingScript) {
      const resolved = Promise.resolve();
      scriptRegistry.set(absoluteSource, resolved);
      return resolved;
    }

    const request = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      Array.from(sourceScript.attributes).forEach((attribute) => {
        if (attribute.name !== 'src' && attribute.name !== 'defer' && attribute.name !== 'async') {
          script.setAttribute(attribute.name, attribute.value);
        }
      });

      script.src = absoluteSource;
      script.async = false;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => {
        reject(new Error(`Unable to load ${absoluteSource}`));
      }, { once: true });
      document.head.appendChild(script);
    });

    scriptRegistry.set(absoluteSource, request);
    request.catch(() => {
      if (scriptRegistry.get(absoluteSource) === request) {
        scriptRegistry.delete(absoluteSource);
      }
    });

    return request;
  };

  const activateQuickViewScripts = async (container) => {
    const scripts = Array.from(container.querySelectorAll('script'));

    for (const sourceScript of scripts) {
      const source = sourceScript.getAttribute('src');

      if (source) {
        sourceScript.remove();

        if (/product-form(?:\.min)?\.js(?:[?#]|$)/i.test(source) || source.toLowerCase().includes('product-form')) {
          continue;
        }

        await loadExternalScript(source, sourceScript);
        continue;
      }

      const script = document.createElement('script');
      Array.from(sourceScript.attributes).forEach((attribute) => {
        script.setAttribute(attribute.name, attribute.value);
      });
      script.textContent = sourceScript.textContent;
      sourceScript.replaceWith(script);
    }
  };

  const getCartRenderer = () => (
    document.querySelector('cart-drawer') || document.querySelector('cart-notification')
  );

  const getCartSections = (cartRenderer) => {
    const sectionIds = [];

    if (cartRenderer && typeof cartRenderer.getSectionsToRender === 'function') {
      try {
        cartRenderer.getSectionsToRender().forEach((item) => {
          const sectionId = item && (item.section || item.id);
          if (sectionId) sectionIds.push(sectionId);
        });
      } catch (error) {
        console.warn('[Quick View] Unable to resolve cart sections.', error);
      }
    }

    if (document.querySelector('cart-drawer')) {
      sectionIds.push('cart-drawer', 'cart-icon-bubble');
    }

    return [...new Set(sectionIds.filter(Boolean))];
  };

  const updateCartCount = (itemCount) => {
    const count = Number(itemCount || 0);

    document.querySelectorAll([
      '[data-cart-count]',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span:first-child',
      '.header__cart-count'
    ].join(',')).forEach((target) => {
      target.textContent = String(count);
    });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      bubble.classList.toggle('hidden', count <= 0);
      bubble.hidden = count <= 0;
    });
  };

  const getFormMessage = (form) => {
    let message = form.querySelector('[data-product-form-message]');

    if (!message) {
      message = document.createElement('p');
      message.className = 'asc-pdp__form-message';
      message.dataset.productFormMessage = '';
      message.hidden = true;
      form.appendChild(message);
    }

    return message;
  };

  const setFormMessage = (form, messageText = '', isError = false) => {
    const message = getFormMessage(form);
    message.textContent = messageText;
    message.classList.toggle('is-error', isError);
    message.hidden = !messageText;
  };

  const clearCartEmptyState = (cartRenderer) => {
    const drawer = document.querySelector('cart-drawer');

    [cartRenderer, drawer].filter(Boolean).forEach((element) => {
      element.classList.remove('is-empty');
      element.querySelector?.('.drawer__inner')?.classList.remove('is-empty');
      element.querySelector?.('cart-drawer-items')?.classList.remove('is-empty');
    });
  };

  const getSectionElement = (html, selector) => {
    if (typeof html !== 'string' || !html.trim()) return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  };

  const requestCartSections = async (sectionIds) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const ids = [...new Set(sectionIds.filter(Boolean))];
    if (!ids.length) return {};

    const response = await fetch(
      `${cartUrl}?sections=${encodeURIComponent(ids.join(','))}&sections_url=${encodeURIComponent(window.location.pathname)}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) throw new Error('Unable to load the updated cart sections.');
    return response.json();
  };

  const renderCartSectionsManually = (sections) => {
    const drawerHtml = sections?.['cart-drawer'];
    const bubbleHtml = sections?.['cart-icon-bubble'];

    if (drawerHtml) {
      const freshDrawer = getSectionElement(drawerHtml, '#CartDrawer');
      const currentDrawer = document.querySelector('#CartDrawer');
      if (freshDrawer && currentDrawer) currentDrawer.innerHTML = freshDrawer.innerHTML;
    }

    if (bubbleHtml) {
      const freshBubble = getSectionElement(bubbleHtml, '#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (freshBubble && currentBubble) currentBubble.innerHTML = freshBubble.innerHTML;
    }
  };

  const renderCartResult = async (cartRenderer, addResponse) => {
    const sectionIds = getCartSections(cartRenderer);
    let sections = addResponse?.sections || {};

    if (document.querySelector('cart-drawer') && typeof sections['cart-drawer'] !== 'string') {
      const freshSections = await requestCartSections([
        ...sectionIds,
        'cart-drawer',
        'cart-icon-bubble'
      ]);
      sections = { ...sections, ...freshSections };
    }

    const renderState = {
      ...(addResponse || {}),
      sections
    };

    clearCartEmptyState(cartRenderer);
    let renderedNatively = false;

    if (
      cartRenderer &&
      typeof cartRenderer.renderContents === 'function' &&
      (typeof sections['cart-drawer'] === 'string' || !document.querySelector('cart-drawer'))
    ) {
      try {
        cartRenderer.setActiveElement?.(document.activeElement);
        cartRenderer.renderContents(renderState);
        renderedNatively = true;
      } catch (error) {
        console.error('[Quick View] Native cart render failed.', error);
      }
    }

    if (!renderedNatively) renderCartSectionsManually(sections);

    clearCartEmptyState(cartRenderer);
    window.requestAnimationFrame(() => clearCartEmptyState(cartRenderer));

    let cart = null;

    try {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (response.ok) cart = await response.json();
    } catch (error) {
      console.warn('[Quick View] Cart state refresh failed.', error);
    }

    if (cart) updateCartCount(cart.item_count);

    const detail = {
      source: 'quick-view',
      cart,
      item: addResponse,
      productVariantId: addResponse?.variant_id || addResponse?.id
    };

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
  };

  class BannerQuickViewController {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector('.asc-quick-view__dialog');
      this.body = modal.querySelector('[data-banner-quick-view-body]');
      this.status = modal.querySelector('[data-banner-quick-view-status]');
      this.content = modal.querySelector('[data-banner-quick-view-content]');
      this.trigger = null;
      this.request = null;
      this.requestId = 0;
      this.closeTimer = null;
      this.addSuccessTimer = null;
      this.cartOpenTimer = null;
      this.productLifecycle = null;
      this.lifecycle = new AbortController();
      this.observedTriggers = new WeakSet();
      this.prefetchedTriggers = new WeakSet();
      this.prefetchObserver = 'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.prefetch(entry.target);
            this.prefetchObserver?.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' })
        : null;

      this.modal.addEventListener('click', (event) => {
        if (!event.target.closest('[data-banner-quick-view-close]')) return;
        event.preventDefault();
        this.close();
      }, { signal: this.lifecycle.signal });

      this.observeTriggers();
    }

    getTriggers(root = document) {
      const candidates = [];

      if (root instanceof Element && root.matches('[data-banner-quick-view-trigger]')) {
        candidates.push(root);
      }

      root.querySelectorAll?.('[data-banner-quick-view-trigger]').forEach((trigger) => {
        candidates.push(trigger);
      });

      return candidates.filter((trigger) => trigger.getAttribute('aria-controls') === this.modal.id);
    }

    observeTriggers(root = document) {
      this.getTriggers(root).forEach((trigger) => {
        if (this.observedTriggers.has(trigger)) return;
        this.observedTriggers.add(trigger);
        this.prefetchObserver?.observe(trigger);
      });
    }

    fetchProductSection(productUrl) {
      return window.ascFetchConfiguredProductSection(productUrl, this.modal.id);
    }

    prefetch(trigger) {
      if (!trigger || this.prefetchedTriggers.has(trigger)) return;
      this.prefetchedTriggers.add(trigger);

      const productUrl = trigger.dataset.productUrl;
      if (productUrl) this.fetchProductSection(productUrl).catch(() => {});
    }

    setStatus(type, productUrl) {
      if (!this.status) return;

      if (type === 'loading') {
        this.content?.classList.remove('is-ready');
        this.content?.setAttribute('aria-hidden', 'true');
        this.status.innerHTML = '<span class="asc-quick-view__spinner" aria-hidden="true"></span><span class="visually-hidden">Loading product</span>';
        this.status.hidden = false;
        return;
      }

      if (type === 'error') {
        this.content?.classList.remove('is-ready');
        this.content?.setAttribute('aria-hidden', 'true');
        this.status.innerHTML = `<div><div>Unable to load this product.</div><a class="asc-quick-view__error-link" href="${productUrl}">View product page</a></div>`;
        this.status.hidden = false;
        return;
      }

      this.content?.classList.add('is-ready');
      this.content?.setAttribute('aria-hidden', 'false');
      this.status.hidden = true;
      this.status.replaceChildren();
    }

    destroyProduct() {
      this.productLifecycle?.abort();
      this.productLifecycle = null;

      if (!this.content) return;
      const productRoot = this.content.querySelector('[id^="AscPdp-"]');
      if (productRoot) productRoot.dispatchEvent(new CustomEvent('asc:pdp:destroy'));
      this.content.classList.remove('is-ready');
      this.content.setAttribute('aria-hidden', 'true');
      this.content.replaceChildren();
    }

    bindProductForm(productRoot) {
      const form = productRoot.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || form.dataset.quickViewSubmitBound === 'true') return;

      form.dataset.quickViewSubmitBound = 'true';
      this.productLifecycle = new AbortController();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = form.querySelector('[type="submit"][name="add"], .asc-pdp__submit');
        if (!submitButton || submitButton.disabled || submitButton.dataset.loading === 'true') return;

        const submitText = submitButton.querySelector('[data-submit-text]');
        const originalText = submitText?.textContent || 'Add to cart';
        const cartRenderer = getCartRenderer();
        const formData = new FormData(form);
        const sections = getCartSections(cartRenderer);

        if (sections.length) {
          formData.set('sections', sections.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        setFormMessage(form);
        submitButton.dataset.loading = 'true';
        submitButton.classList.add('is-loading');
        submitButton.setAttribute('aria-disabled', 'true');
        if (submitText) submitText.textContent = 'Adding...';

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData,
            credentials: 'same-origin'
          });

          const result = await response.json().catch(() => ({}));
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
          }

          await renderCartResult(cartRenderer, result);
          if (submitText) submitText.textContent = 'Added';

          if (this.addSuccessTimer) window.clearTimeout(this.addSuccessTimer);
          this.addSuccessTimer = window.setTimeout(() => {
            this.addSuccessTimer = null;
            this.close();

            const drawer = document.querySelector('cart-drawer');
            const notification = document.querySelector('cart-notification');
            const activeCart = drawer || notification;

            if (activeCart && typeof activeCart.open === 'function') {
              if (this.cartOpenTimer) window.clearTimeout(this.cartOpenTimer);
              this.cartOpenTimer = window.setTimeout(() => {
                this.cartOpenTimer = null;
                activeCart.open();
              }, 50);
            }
          }, 250);
        } catch (error) {
          console.error('[Quick View] Add to cart failed.', error);
          setFormMessage(form, error.message || 'Unable to add this item to the cart.', true);
          if (submitText) submitText.textContent = originalText;
        } finally {
          if (submitButton.isConnected) {
            submitButton.dataset.loading = 'false';
            submitButton.classList.remove('is-loading');
            submitButton.removeAttribute('aria-disabled');
          }
        }
      }, { capture: true, signal: this.productLifecycle.signal });
    }

    async initializeProduct() {
      if (!this.content) return;

      const productRoot = this.content.querySelector('[id^="AscPdp-"]');
      if (!productRoot) throw new Error('Injected product root was not found');

      productRoot.dataset.updateUrl = 'false';
      productRoot.dataset.quickView = 'true';

      await activateQuickViewScripts(this.content);
      this.bindProductForm(productRoot);

      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();

      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        detail: { sectionId: productRoot.dataset.sectionId || '' }
      }));
    }

    async open(trigger) {
      if (!this.modal || !this.content || !this.dialog) return;

      const productUrl = trigger.dataset.productUrl;
      if (!productUrl) return;

      if (activeController && activeController !== this) {
        activeController.close({ restoreFocus: false, immediate: true });
      }

      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.requestId += 1;
      const requestId = this.requestId;
      this.request?.abort();
      this.request = new AbortController();
      this.trigger = trigger;
      activeController = this;

      this.destroyProduct();
      this.setStatus('loading', productUrl);
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('asc-quick-view-open');
      document.body.classList.add('asc-quick-view-open');
      window.requestAnimationFrame(() => this.modal.classList.add('is-open'));
      this.dialog.focus({ preventScroll: true });

      try {
        const markup = await waitForRequest(
          this.fetchProductSection(productUrl),
          this.request.signal
        );

        if (requestId !== this.requestId || this.request.signal.aborted) return;

        this.content.innerHTML = markup;
        await this.initializeProduct();
        if (requestId !== this.requestId) return;

        this.setStatus('ready', productUrl);
        this.body?.scrollTo({ top: 0, behavior: 'auto' });

        const title = this.content.querySelector('.asc-pdp__title')?.textContent?.trim();
        if (title) this.dialog.setAttribute('aria-label', `Quick View: ${title}`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Quick View]', error);
        this.destroyProduct();
        this.setStatus('error', productUrl);
      }
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (!this.modal || this.modal.hidden) return;

      this.requestId += 1;
      this.request?.abort();
      this.request = null;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');

      if (activeController === this) activeController = null;
      document.documentElement.classList.remove('asc-quick-view-open');
      document.body.classList.remove('asc-quick-view-open');

      const finish = () => {
        this.modal.hidden = true;
        this.destroyProduct();
        this.setStatus('ready', '');
        this.dialog?.setAttribute('aria-label', 'Quick View');
        this.closeTimer = null;
      };

      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      if (immediate) finish();
      else this.closeTimer = window.setTimeout(finish, 220);

      if (restoreFocus && this.trigger && typeof this.trigger.focus === 'function') {
        this.trigger.focus({ preventScroll: true });
      }
      this.trigger = null;
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.modal.classList.contains('is-open')) return;

      const focusable = Array.from(this.modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      if (this.addSuccessTimer) window.clearTimeout(this.addSuccessTimer);
      if (this.cartOpenTimer) window.clearTimeout(this.cartOpenTimer);
      this.request?.abort();
      this.request = null;
      this.prefetchObserver?.disconnect();
      this.lifecycle.abort();

      if (!this.modal.hidden) this.close({ restoreFocus: false, immediate: true });
      else this.destroyProduct();
    }
  }

  const getControllerForTrigger = (trigger) => {
    const modalId = trigger?.getAttribute('aria-controls');
    if (!modalId) return null;

    let controller = controllers.get(modalId);
    if (controller) return controller;

    const modal = document.getElementById(modalId);
    if (!modal?.matches('[data-banner-quick-view-modal]')) return null;

    controller = new BannerQuickViewController(modal);
    controllers.set(modalId, controller);
    return controller;
  };

  const mount = (root = document) => {
    const modals = [];

    if (root instanceof Element && root.matches('[data-banner-quick-view-modal]')) {
      modals.push(root);
    }

    root.querySelectorAll?.('[data-banner-quick-view-modal]').forEach((modal) => {
      modals.push(modal);
    });

    modals.forEach((modal) => {
      let controller = controllers.get(modal.id);

      if (controller && controller.modal !== modal) {
        controller.destroy();
        controllers.delete(modal.id);
        controller = null;
      }

      if (!controller) {
        controller = new BannerQuickViewController(modal);
        controllers.set(modal.id, controller);
      } else {
        controller.observeTriggers(root);
      }
    });
  };

  const unmount = (root) => {
    controllers.forEach((controller, modalId) => {
      if (root !== controller.modal && !root?.contains?.(controller.modal)) return;
      controller.destroy();
      controllers.delete(modalId);
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-banner-quick-view-trigger]');
    if (!trigger) return;

    const controller = getControllerForTrigger(trigger);
    if (!controller) return;

    event.preventDefault();
    event.stopPropagation();
    controller.open(trigger);
  }, { signal: lifecycle.signal });

  ['pointerover', 'focusin', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const trigger = event.target.closest?.('[data-banner-quick-view-trigger]');
      if (!trigger) return;
      getControllerForTrigger(trigger)?.prefetch(trigger);
    }, { passive: true, signal: lifecycle.signal });
  });

  document.addEventListener('keydown', (event) => {
    if (!activeController) return;

    if (event.key === 'Escape') {
      const nestedGuideOpen = activeController.modal.querySelector('.asc-pdp__modal:not([hidden])');
      if (!nestedGuideOpen) activeController.close();
      return;
    }

    activeController.trapFocus(event);
  }, { signal: lifecycle.signal });

  document.addEventListener('shopify:section:load', (event) => {
    mount(event.target instanceof Element ? event.target : document);
  }, { signal: lifecycle.signal });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element) unmount(event.target);
  }, { signal: lifecycle.signal });

  const api = {
    mount,
    unmount,
    destroy() {
      controllers.forEach((controller) => controller.destroy());
      controllers.clear();
      lifecycle.abort();
      activeController = null;
      document.documentElement.classList.remove('asc-quick-view-open');
      document.body.classList.remove('asc-quick-view-open');
      delete window.ASCBannerQuickView;
    }
  };

  window.ASCBannerQuickView = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), {
      once: true,
      signal: lifecycle.signal
    });
  } else {
    mount(document);
  }
})();
